// [ai] What the widget host shows the seam and the frames: every mounted frame's status (loading → ready | error | timeout) and
// which widget holds media focus. Written by widgets/widgets.ts and the frames; read by the frames and window.__dossier.dump().
import { createStore, useStore, type StoreApi } from "zustand";
import { createContext, useContext } from "react";
export type FrameStatus = "loading" | "ready" | "error" | "timeout";
export type FrameRow = { id: string; kind: string; status: FrameStatus; error?: string };
export type WidgetsState = {
  frames: Record<string, FrameRow>; focus: string | null;
  register: (key: string, kind: string) => void; unregister: (key: string) => void;
  status: (key: string, status: FrameStatus, error?: string) => void; setFocus: (id: string | null) => void;
};
export function createWidgetsStore(): StoreApi<WidgetsState> {
  return createStore<WidgetsState>(set => ({
    frames: {}, focus: null,
    register: (key, kind) => set(s => ({ frames: { ...s.frames, [key]: { id: key, kind, status: "loading" } } })),
    unregister: key => set(s => { const frames = { ...s.frames }; delete frames[key]; return { frames }; }),
    status: (key, status, error) => set(s => { const f = s.frames[key]; if (!f || f.status === status && f.error === error) return s; const row: FrameRow = error === undefined ? { id: f.id, kind: f.kind, status } : { id: f.id, kind: f.kind, status, error }; return { frames: { ...s.frames, [key]: row } }; }),
    setFocus: focus => set(s => (s.focus === focus ? s : { focus })),
  }));
}
export const WidgetsStoreContext = createContext<StoreApi<WidgetsState> | null>(null);
export function useWidgets<T>(selector: (s: WidgetsState) => T): T { const s = useContext(WidgetsStoreContext); if (!s) throw new Error("WidgetsStoreContext is missing"); return useStore(s, selector); }
